import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { EmployeeSelect } from "@/components/hr/EmployeeSelect";
import { emptyLoanPaymentDraft, LoanPaymentMethodFields } from "@/components/hr/LoanPaymentMethodFields";
import {
  SALARY_PERIOD_MIN_YEAR,
  SalaryPeriodMonthPicker,
} from "@/components/hr/SalaryPeriodMonthPicker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { formatAmountCommas } from "@/lib/formatPkrAmount";
import { getEmployeeById, listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import {
  createEmployeeLoan,
  listEmployeeLoans,
  loansApiErrorMessage,
  updateEmployeeLoan,
  validateLoanPayment,
  type EmployeeLoanRecord,
} from "@/lib/hr/loansStore";
import type { ExpensePaymentMethod } from "@/lib/expenses/expensesStore";
import { fetchBanks, type BankOption } from "@/lib/sales/banksStore";
import { addMonths, format, isValid, parseISO } from "date-fns";
import { Calculator, CheckCircle2, HandCoins, History, Info, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const LOAN_PERIOD_MAX_YEAR = () => new Date().getFullYear() + 5;

function monthStartIso(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}-01`;
}

function parseMonthParts(iso: string): { year: number; month: number } | null {
  if (!iso.trim()) return null;
  const dt = parseISO(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (!isValid(dt)) return null;
  return { year: dt.getFullYear(), month: dt.getMonth() + 1 };
}

function currentMonthStartIso(): string {
  const now = new Date();
  return monthStartIso(now.getFullYear(), now.getMonth() + 1);
}

function parseMoneyAmount(v: string | number | null | undefined): number {
  if (v == null) return 0;
  const n = Number(String(v).replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function loanLastInstallmentMonth(row: EmployeeLoanRecord): Date | null {
  const start = parseISO(row.startDate.length <= 10 ? `${row.startDate}T00:00:00` : row.startDate);
  if (!isValid(start)) return null;
  const total = Math.max(1, Number(row.totalInstallments) || 1);
  return addMonths(start, total - 1);
}

/** Month after the latest active loan’s final installment (yyyy-MM-01), or null. */
function suggestedStartAfterPreviousLoans(loans: EmployeeLoanRecord[]): string | null {
  const active = loans.filter((r) => r.status === "active" && parseMoneyAmount(r.remainingAmount) > 0.005);
  if (active.length === 0) return null;

  let latestEnd: Date | null = null;
  for (const row of active) {
    const end = loanLastInstallmentMonth(row);
    if (!end) continue;
    if (!latestEnd || end > latestEnd) latestEnd = end;
  }
  if (!latestEnd) return null;
  const next = addMonths(latestEnd, 1);
  return monthStartIso(next.getFullYear(), next.getMonth() + 1);
}

function formatMonthLabel(isoOrDate: string | Date | null | undefined): string | null {
  if (isoOrDate == null) return null;
  const dt = typeof isoOrDate === "string" ? parseISO(isoOrDate.length <= 10 ? `${isoOrDate}T00:00:00` : isoOrDate) : isoOrDate;
  return isValid(dt) ? format(dt, "MMM yyyy") : null;
}

type Draft = {
  loanAmount: string;
  installmentAmount: string;
  tenureMonths: string;
  loanDate: string;
  startDate: string;
  paymentMethod: ReturnType<typeof emptyLoanPaymentDraft>["paymentMethod"];
  bankId: string;
  referenceNo: string;
  chequeNo: string;
  chequeDate: string;
};

const emptyDraft = (): Draft => ({
  loanAmount: "",
  installmentAmount: "",
  tenureMonths: "",
  loanDate: format(new Date(), "yyyy-MM-dd"),
  startDate: currentMonthStartIso(),
  ...emptyLoanPaymentDraft(),
});

function formatMoney(value: string): string {
  const raw = value.replace(/[^\d.]/g, "");
  if (!raw) return value;
  const [whole, ...rest] = raw.split(".");
  const formatted = formatAmountCommas(whole.replace(/\D/g, ""));
  return rest.length ? `${formatted}.${rest.join("")}` : formatted;
}

function safeNumber(v: string | number | null | undefined): number | null {
  if (v == null) return null;
  const raw = String(v).replace(/[^\d.]/g, "");
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function commaFormat(n: number | null | undefined): string {
  if (n == null) return "0";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
}

export type EmployeeLoanCreateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, employee is locked (employee profile). Omit on /payroll/loans so the user can pick one. */
  employeeId?: string;
  /** When set, the dialog edits this loan instead of creating a new one. */
  loan?: EmployeeLoanRecord | null;
  onSaved?: () => void;
};

export function EmployeeLoanCreateDialog({
  open,
  onOpenChange,
  employeeId,
  loan,
  onSaved,
}: EmployeeLoanCreateDialogProps) {
  const editing = loan ?? null;
  const lockedEmployeeId = editing?.employeeId || employeeId || "";
  const allowEmployeeSelect = !lockedEmployeeId;

  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(lockedEmployeeId);
  const [saving, setSaving] = useState(false);
  const [startMonthOpen, setStartMonthOpen] = useState(false);
  const [employee, setEmployee] = useState<EmployeeRecord | undefined>(() =>
    getEmployeeById(lockedEmployeeId || selectedEmployeeId),
  );
  const [banks, setBanks] = useState<BankOption[]>([]);
  const [empTick, setEmpTick] = useState(0);
  const [historyRows, setHistoryRows] = useState<EmployeeLoanRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const employees = useMemo(() => listEmployees(), [empTick]);

  useEffect(() => {
    if (!open) return;

    if (editing) {
      setSelectedEmployeeId(editing.employeeId);
      setEmployee(getEmployeeById(editing.employeeId));
      const startParts = parseMonthParts(editing.startDate);
      setDraft({
        loanAmount: formatMoney(editing.loanAmount),
        installmentAmount: formatMoney(editing.installmentAmount),
        tenureMonths: String(Math.max(1, Number(editing.totalInstallments) || 1)),
        loanDate: editing.loanDate || editing.startDate,
        startDate: startParts ? monthStartIso(startParts.year, startParts.month) : currentMonthStartIso(),
        paymentMethod: editing.paymentMethod,
        bankId: editing.bankId,
        referenceNo: editing.referenceNo,
        chequeNo: editing.chequeNo,
        chequeDate: editing.chequeDate,
      });
      return;
    }

    setDraft(emptyDraft());
    setSelectedEmployeeId(employeeId || "");
    setEmployee(employeeId ? getEmployeeById(employeeId) : undefined);
    setStartMonthOpen(false);
    setHistoryRows([]);
  }, [open, employeeId, loan?.id]);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    void fetchBanks()
      .then((rows) => {
        if (mounted) setBanks(rows);
      })
      .catch(() => {
        if (mounted) setBanks([]);
      });
    return () => {
      mounted = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !allowEmployeeSelect) return;
    void syncEmployeesFromApi()
      .catch(() => {})
      .finally(() => setEmpTick((t) => t + 1));
  }, [open, allowEmployeeSelect]);

  useEffect(() => {
    if (!selectedEmployeeId) {
      setEmployee(undefined);
      return;
    }
    setEmployee(getEmployeeById(selectedEmployeeId) ?? employees.find((e) => e.id === selectedEmployeeId));
  }, [selectedEmployeeId, employees]);

  useEffect(() => {
    if (!open || !selectedEmployeeId) {
      setHistoryRows([]);
      setHistoryLoading(false);
      return;
    }

    let mounted = true;
    setHistoryLoading(true);
    void listEmployeeLoans({ employeeId: selectedEmployeeId, page: 1, perPage: 50 })
      .then((res) => {
        if (!mounted) return;
        const rows = editing ? res.rows.filter((r) => r.id !== editing.id) : res.rows;
        setHistoryRows(rows);
        if (!editing) {
          const suggested = suggestedStartAfterPreviousLoans(rows);
          if (suggested) {
            setDraft((prev) => ({ ...prev, startDate: suggested }));
          }
        }
      })
      .catch(() => {
        if (mounted) setHistoryRows([]);
      })
      .finally(() => {
        if (mounted) setHistoryLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [open, selectedEmployeeId, editing?.id]);

  const previousLoans = historyRows;
  const activePreviousLoans = useMemo(
    () => previousLoans.filter((r) => r.status === "active"),
    [previousLoans],
  );
  const suggestedStartDate = useMemo(
    () => (editing ? null : suggestedStartAfterPreviousLoans(previousLoans)),
    [editing, previousLoans],
  );
  const suggestedStartLabel = useMemo(() => formatMonthLabel(suggestedStartDate), [suggestedStartDate]);
  const previousLastInstallmentLabel = useMemo(() => {
    const active = previousLoans.filter((r) => r.status === "active" && parseMoneyAmount(r.remainingAmount) > 0.005);
    let latestEnd: Date | null = null;
    for (const row of active) {
      const end = loanLastInstallmentMonth(row);
      if (!end) continue;
      if (!latestEnd || end > latestEnd) latestEnd = end;
    }
    return formatMonthLabel(latestEnd);
  }, [previousLoans]);
  const outstandingPrevious = useMemo(
    () =>
      activePreviousLoans.reduce((sum, r) => {
        const n = parseMoneyAmount(r.remainingAmount);
        return sum + n;
      }, 0),
    [activePreviousLoans],
  );

  const loanNum = useMemo(() => safeNumber(draft.loanAmount), [draft.loanAmount]);
  const instNum = useMemo(() => safeNumber(draft.installmentAmount), [draft.installmentAmount]);
  const tenureNum = useMemo(() => {
    const n = Number(String(draft.tenureMonths).replace(/[^\d]/g, ""));
    return Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
  }, [draft.tenureMonths]);

  const installmentsCount = useMemo(() => {
    if (tenureNum) return tenureNum;
    if (!loanNum || !instNum) return null;
    return Math.max(1, Math.ceil(loanNum / instNum));
  }, [tenureNum, loanNum, instNum]);

  const completionDateFormatted = useMemo(() => {
    if (!installmentsCount || !draft.startDate.trim()) return null;
    const dt = parseISO(draft.startDate);
    if (!isValid(dt)) return null;
    const endDt = addMonths(dt, installmentsCount - 1);
    return format(endDt, "MMM yyyy");
  }, [installmentsCount, draft.startDate]);

  const basicSalaryNum = employee?.basicSalary ? Number(String(employee.basicSalary).replace(/,/g, "")) : 0;
  const employeeName = employee
    ? employee.fullName || `${employee.firstName} ${employee.lastName}`.trim() || "Employee"
    : "Employee";
  const employeeInitial = employeeName.trim().charAt(0).toUpperCase() || "E";

  const onTenureChange = (raw: string) => {
    const digits = raw.replace(/[^\d]/g, "");
    const months = Number(digits);
    setDraft((prev) => {
      const next: Draft = { ...prev, tenureMonths: digits };
      if (loanNum && Number.isFinite(months) && months >= 1) {
        next.installmentAmount = formatAmountCommas(String(Math.max(1, Math.round(loanNum / months))));
      }
      return next;
    });
  };

  const onInstallmentChange = (raw: string) => {
    const formatted = formatMoney(raw);
    const nextInst = safeNumber(formatted);
    setDraft((prev) => {
      const next: Draft = { ...prev, installmentAmount: formatted };
      if (loanNum && nextInst) {
        next.tenureMonths = String(Math.max(1, Math.ceil(loanNum / nextInst)));
      }
      return next;
    });
  };

  const onLoanAmountChange = (raw: string) => {
    const formatted = formatMoney(raw);
    const nextLoan = safeNumber(formatted);
    setDraft((prev) => {
      const next: Draft = { ...prev, loanAmount: formatted };
      const tenure = Number(prev.tenureMonths) >= 1 ? Math.floor(Number(prev.tenureMonths)) : null;
      if (nextLoan && tenure) {
        next.installmentAmount = formatAmountCommas(String(Math.max(1, Math.round(nextLoan / tenure))));
      }
      return next;
    });
  };

  const applyAmountPreset = (amt: number) => {
    setDraft((prev) => {
      const tenure = Number(prev.tenureMonths) >= 1 ? Math.floor(Number(prev.tenureMonths)) : 6;
      return {
        ...prev,
        loanAmount: formatAmountCommas(String(amt)),
        tenureMonths: String(tenure),
        installmentAmount: formatAmountCommas(String(Math.max(1, Math.round(amt / tenure)))),
      };
    });
  };

  const close = () => onOpenChange(false);

  const save = async () => {
    if (saving) return;
    const resolvedEmployeeId = selectedEmployeeId || lockedEmployeeId;
    if (!resolvedEmployeeId) return toast.error("Select an employee.");
    if (!loanNum || loanNum <= 0) return toast.error("Loan amount must be a number > 0.");
    if (!instNum || instNum <= 0) return toast.error("Installment amount must be a number > 0.");
    if (!tenureNum || tenureNum < 1) return toast.error("Tenure (months) must be a whole number ≥ 1.");
    if (!draft.loanDate.trim()) return toast.error("Loan date is required.");
    if (!draft.startDate.trim() || !parseMonthParts(draft.startDate)) {
      return toast.error("Installment start month is required.");
    }
    const paymentMethod = (draft.paymentMethod || "") as ExpensePaymentMethod | "";
    const paymentError = validateLoanPayment({
      paymentMethod,
      bankId: draft.bankId,
      referenceNo: draft.referenceNo,
      chequeNo: draft.chequeNo,
    });
    if (paymentError) return toast.error(paymentError);

    setSaving(true);
    try {
      if (editing) {
        const result = await updateEmployeeLoan({
          employeeId: resolvedEmployeeId,
          id: editing.id,
          patch: {
            installmentAmount: instNum,
            loanDate: draft.loanDate,
            startDate: draft.startDate,
            paymentMethod,
            bankId: draft.bankId,
            referenceNo: draft.referenceNo,
            chequeNo: draft.chequeNo,
            chequeDate: draft.chequeDate,
          },
        });
        toast.success(result.message);
      } else {
        const result = await createEmployeeLoan({
          employeeId: resolvedEmployeeId,
          loanAmount: loanNum,
          installmentAmount: instNum,
          loanDate: draft.loanDate,
          startDate: draft.startDate,
          paymentMethod,
          bankId: draft.bankId,
          referenceNo: draft.referenceNo,
          chequeNo: draft.chequeNo,
          chequeDate: draft.chequeDate,
        });
        toast.success(result.message);
      }
      close();
      onSaved?.();
    } catch (e) {
      toast.error(loansApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
        <DialogHeader className="pb-3 border-b border-border/50">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600">
              <HandCoins className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-foreground">
                {editing ? "Edit Employee Loan" : "Issue Employee Loan"}
              </DialogTitle>
              <p className="text-xs text-muted-foreground">
                {editing
                  ? "Update the monthly installment, first deduction date, and disbursement method."
                  : "Sanction a company loan with automated monthly payroll installment deductions."}
              </p>
            </div>
          </div>
        </DialogHeader>

        {allowEmployeeSelect ? (
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-foreground">
              Employee <span className="text-destructive">*</span>
            </Label>
            <EmployeeSelect
              value={selectedEmployeeId}
              onChange={(id) => {
                setSelectedEmployeeId(id);
                setEmployee(employees.find((e) => e.id === id) ?? getEmployeeById(id));
                setDraft(emptyDraft());
              }}
              employees={employees}
              triggerClassName="h-10"
            />
          </div>
        ) : null}

        {employee ? (
          <div className="flex items-center justify-between bg-muted/30 border border-border/50 rounded-xl p-3 text-xs">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="h-8 w-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center font-bold text-primary text-xs overflow-hidden shrink-0">
                {employee.photoDataUrl ? (
                  <img src={employee.photoDataUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  employeeInitial
                )}
              </div>
              <div className="min-w-0">
                <p className="font-bold text-foreground truncate">{employeeName}</p>
                <p className="text-[11px] text-muted-foreground truncate">
                  {employee.designationName || "Staff"} · {employee.employeeCode || "—"}
                </p>
              </div>
            </div>
            <div className="text-right shrink-0 pl-3">
              <p className="text-[10px] uppercase font-semibold text-muted-foreground">Basic Monthly Salary</p>
              <p className="font-bold text-primary">{formatCurrency(Number.isFinite(basicSalaryNum) ? basicSalaryNum : 0)}</p>
            </div>
          </div>
        ) : null}

        {selectedEmployeeId ? (
          <div className="rounded-xl border border-border/50 bg-muted/15 p-3 space-y-2">
            <div className="flex items-center gap-2 min-w-0">
              <History className="w-4 h-4 text-primary shrink-0" />
              <h4 className="font-bold text-xs uppercase tracking-wider text-foreground">Previous loan schedule</h4>
            </div>

            {historyLoading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Loading loan history…
              </div>
            ) : previousLastInstallmentLabel && suggestedStartLabel ? (
              <div className="rounded-lg border border-amber-300/60 bg-amber-50/50 dark:bg-amber-950/20 px-3 py-2 space-y-1 text-xs">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-muted-foreground">
                    Last installment:{" "}
                    <span className="font-semibold text-foreground">{previousLastInstallmentLabel}</span>
                  </p>
                  <Badge
                    variant="default"
                    className="shrink-0 bg-amber-500/15 text-amber-800 dark:text-amber-300 border-amber-300/40 hover:bg-amber-500/15 pointer-events-none"
                  >
                    Active remaining {formatCurrency(outstandingPrevious)}
                  </Badge>
                </div>
                <p className="text-[11px] text-amber-800 dark:text-amber-300">
                  Next loan installment should start from{" "}
                  <span className="font-semibold">{suggestedStartLabel}</span> onward.
                </p>
              </div>
            ) : previousLoans.length === 0 ? (
              <p className="rounded-lg border border-border/40 bg-background/60 px-3 py-2 text-xs text-muted-foreground">
                No previous loans for this employee.
              </p>
            ) : (
              <p className="rounded-lg border border-border/40 bg-background/60 px-3 py-2 text-xs text-muted-foreground">
                No active loan with remaining installments.
              </p>
            )}
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-foreground">
                Sanctioned Loan Amount <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Input
                  className="glass-input h-10 pr-12 font-semibold"
                  value={draft.loanAmount}
                  onChange={(e) => onLoanAmountChange(e.target.value)}
                  placeholder="e.g. 50,000"
                  inputMode="decimal"
                  disabled={!!editing}
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
                  PKR
                </span>
              </div>
              {!editing ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] text-muted-foreground shrink-0">Quick:</span>
                {basicSalaryNum > 0 ? (
                  <button
                    type="button"
                    onClick={() => applyAmountPreset(basicSalaryNum)}
                    className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-md border border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                  >
                    1x
                  </button>
                ) : null}
                {[25000, 50000, 100000].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => applyAmountPreset(preset)}
                    className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-md border border-border/60 bg-background hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-colors"
                  >
                    +{commaFormat(preset)}
                  </button>
                ))}
              </div>
              ) : (
                <p className="text-[11px] text-muted-foreground">Loan amount is fixed after creation.</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_0.9fr] gap-3 items-start">
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-foreground">
                  Monthly Installment <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <Input
                    className="glass-input h-10 pr-12 font-semibold"
                    value={draft.installmentAmount}
                    onChange={(e) => onInstallmentChange(e.target.value)}
                    placeholder="e.g. 5,000"
                    inputMode="decimal"
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
                    PKR
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-foreground">
                  Tenure <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <Input
                    className="glass-input h-10 pr-12 font-semibold tabular-nums"
                    value={draft.tenureMonths}
                    onChange={(e) => onTenureChange(e.target.value)}
                    placeholder="e.g. 6"
                    inputMode="numeric"
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
                    Mo
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-foreground">
                    Loan Date <span className="text-destructive">*</span>
                  </Label>
                  <DatePartsPicker
                    value={draft.loanDate}
                    onChange={(iso) => setDraft((x) => ({ ...x, loanDate: iso }))}
                    fromYear={1980}
                    toYear={new Date().getFullYear() + 1}
                    placeholder="Select loan date"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-foreground">
                    Installment start <span className="text-destructive">*</span>
                  </Label>
                  <SalaryPeriodMonthPicker
                    open={startMonthOpen}
                    onOpenChange={setStartMonthOpen}
                    selectedYear={parseMonthParts(draft.startDate)?.year ?? null}
                    selectedMonth={parseMonthParts(draft.startDate)?.month ?? null}
                    onSelect={(year, month) =>
                      setDraft((x) => ({ ...x, startDate: monthStartIso(year, month) }))
                    }
                    minYear={SALARY_PERIOD_MIN_YEAR}
                    maxYear={LOAN_PERIOD_MAX_YEAR()}
                    placeholder="Select month"
                    title="Select installment month"
                    description="First payroll month when loan installment deduction begins."
                    showSelectedFooter={false}
                  />
                </div>
              </div>
              {suggestedStartLabel ? (
                <p className="text-[11px] text-amber-700 dark:text-amber-300">
                  Suggested start from <span className="font-semibold">{suggestedStartLabel}</span> onward
                  {previousLastInstallmentLabel ? (
                    <> (after previous last installment {previousLastInstallmentLabel})</>
                  ) : null}
                  . Earlier months stay selectable if needed.
                </p>
              ) : null}

              <LoanPaymentMethodFields
                value={draft}
                banks={banks}
                onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
              />
            </div>
          </div>

          <div className="flex flex-col justify-between rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-4">
            <div>
              <div className="flex items-center gap-2 pb-2">
                <Calculator className="w-4 h-4 text-primary" />
                <h4 className="font-bold text-xs uppercase tracking-wider text-foreground">Live Schedule Preview</h4>
              </div>

              <div className="space-y-3 pt-3 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Already outstanding</span>
                  <span className="font-semibold tabular-nums text-amber-700 dark:text-amber-400">
                    {historyLoading
                      ? "…"
                      : selectedEmployeeId
                        ? formatCurrency(outstandingPrevious)
                        : "—"}
                    {!historyLoading && activePreviousLoans.length > 0 ? (
                      <span className="text-[10px] text-muted-foreground font-normal">
                        {" "}
                        ({activePreviousLoans.length})
                      </span>
                    ) : null}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Sanctioned Amount</span>
                  <span className="font-bold text-foreground text-sm">{loanNum ? formatCurrency(loanNum) : "—"}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Monthly Deduction</span>
                  <span className="font-bold text-rose-600">{instNum ? `-${formatCurrency(instNum)}` : "—"}</span>
                </div>
                <div className="flex justify-between items-center border-t border-primary/10 pt-2">
                  <span className="text-muted-foreground">Total Installments</span>
                  <span className="font-extrabold text-primary text-sm">
                    {installmentsCount ? `${installmentsCount} Months` : "—"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Estimated Payoff</span>
                  <span className="font-bold text-foreground">{completionDateFormatted || "—"}</span>
                </div>

                {installmentsCount && loanNum && instNum ? (
                  <div className="bg-background/80 rounded-lg border border-primary/20 p-3 space-y-2 mt-2">
                    <div className="flex justify-between text-[11px] font-bold">
                      <span className="text-muted-foreground">Monthly Deduction Impact</span>
                      <span className="text-primary">
                        {((instNum / (basicSalaryNum || loanNum)) * 100).toFixed(1)}% of basic
                      </span>
                    </div>
                    <div className="h-2 w-full bg-primary/20 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${Math.min(100, Math.round((instNum / (basicSalaryNum || loanNum)) * 100))}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground italic text-center pt-0.5">
                      Deducted automatically from monthly salary payslips starting{" "}
                      {isValid(parseISO(draft.startDate)) ? format(parseISO(draft.startDate), "MMM yyyy") : "—"}.
                    </p>
                  </div>
                ) : (
                  <div className="bg-background/50 rounded-lg border border-border/50 p-3 text-center text-xs text-muted-foreground italic">
                    Enter loan and installment amounts to view live schedule breakdown.
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pt-2 border-t border-primary/10">
              <Info className="w-3.5 h-3.5 text-primary shrink-0" />
              <span>Loan balance updates automatically as payroll batches are finalized.</span>
            </div>
          </div>
        </div>

        <DialogFooter className="pt-4 border-t border-border/50 gap-2">
          <Button variant="outline" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void save()}
            disabled={saving || !selectedEmployeeId || !loanNum || !instNum}
            className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {editing ? "Saving..." : "Sanctioning Loan..."}
              </>
            ) : editing ? (
              "Save Loan"
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Sanction Loan
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
